/**
 * Operation-specific parameters editor for transformation steps.
 * Renders contextual form fields based on the selected operation type.
 */

import React from 'react';
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

interface ParametersEditorProps {
  operation: string;
  parameters: Record<string, unknown>;
  onChange: (params: Record<string, unknown>) => void;
  availableFields: string[];
}

const ParametersEditor: React.FC<ParametersEditorProps> = ({
  operation,
  parameters,
  onChange,
  availableFields,
}) => {
  switch (operation) {
    case 'aggregate':
      return (
        <Stack spacing={1}>
          <FormControl size="small" fullWidth>
            <InputLabel>Aggregate Function</InputLabel>
            <Select
              value={(parameters.function as string) || ''}
              label="Aggregate Function"
              onChange={(e) => onChange({ ...parameters, function: e.target.value })}
            >
              {['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COUNT_DISTINCT'].map((fn) => (
                <MenuItem key={fn} value={fn}>{fn}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            multiple
            options={availableFields}
            value={(parameters.group_by as string[]) || []}
            onChange={(_, value) => onChange({ ...parameters, group_by: value })}
            renderInput={(params) => <TextField {...params} label="GROUP BY" />}
          />
        </Stack>
      );
    case 'window':
      return (
        <Stack spacing={1}>
          <TextField
            size="small"
            label="Window Function"
            value={(parameters.function as string) || ''}
            fullWidth
            onChange={(e) => onChange({ ...parameters, function: e.target.value })}
          />
          <Autocomplete
            size="small"
            multiple
            options={availableFields}
            value={(parameters.partition_by as string[]) || []}
            onChange={(_, value) => onChange({ ...parameters, partition_by: value })}
            renderInput={(p) => <TextField {...p} label="PARTITION BY" />}
          />
          <Autocomplete
            size="small"
            multiple
            options={availableFields}
            value={(parameters.order_by as string[]) || []}
            onChange={(_, value) => onChange({ ...parameters, order_by: value })}
            renderInput={(p) => <TextField {...p} label="ORDER BY" />}
          />
        </Stack>
      );
    case 'filter':
      return (
        <TextField
          size="small"
          label="WHERE clause"
          fullWidth
          value={(parameters.where as string) || ''}
          onChange={(e) => onChange({ ...parameters, where: e.target.value })}
          sx={{ '& input': { fontFamily: 'monospace', fontSize: 12 } }}
        />
      );
    case 'set_constant':
      return (
        <TextField
          size="small"
          label="Constant Value"
          fullWidth
          value={(parameters.value as string) || ''}
          onChange={(e) => onChange({ ...parameters, value: e.target.value })}
        />
      );
    case 'unpivot':
      return (
        <Stack spacing={1}>
          <Autocomplete
            size="small"
            multiple
            options={availableFields}
            value={(parameters.columns as string[]) || []}
            onChange={(_, value) => onChange({ ...parameters, columns: value })}
            renderInput={(p) => <TextField {...p} label="Columns to Unpivot" />}
          />
          <TextField
            size="small"
            label="Name Column"
            value={(parameters.name_column as string) || ''}
            fullWidth
            onChange={(e) => onChange({ ...parameters, name_column: e.target.value })}
          />
          <TextField
            size="small"
            label="Value Column"
            value={(parameters.value_column as string) || ''}
            fullWidth
            onChange={(e) => onChange({ ...parameters, value_column: e.target.value })}
          />
        </Stack>
      );
    default:
      return null;
  }
};

export default ParametersEditor;
